import { MiDialogOptions } from '@/components/MiDialog/MiDialog';
import { User as UserModel, CreateUserPayload } from '@/models/User';
import { Component, Vue } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { AxiosError } from 'axios';

import { openDialog } from '@/support/Dialog';
import { object as dotObject } from 'dot-object';
import MiForm, { MiFormField } from '@/components/MiForm/MiForm';
import { merge } from 'lodash';
import { getStatusColor, getStatusLabel } from '@/support/ReportStatus';
import { PusherService } from '@/support/PusherService';
import { Application } from '@/support/Application';
import { UserSyncFailed, UserSyncFinished, UserSyncStarted } from '@/support/Pusher/UserSync';

@Component<User>({})
export default class User extends Vue {
  protected $pageTitle = 'Gebruiker';

  protected activeTab = 'actions';

  protected currentPath = '';

  protected isFormValid = true;

  protected editedFormFields: MiFormField[] = [];

  protected user: UserModel | null = null;

  protected isLoading = true;

  protected isSyncing = false;

  protected isEditing = false;

  protected async mounted(): Promise<void> {
    this.setActiveTab();
    await this.initialize();
    this.initializePusher();
    this.isLoading = false;
  }

  protected async initialize(): Promise<void> {
    await this.fetchReport();
  }

  protected async fetchReport(): Promise<void> {
    if (! this.$route.params.id) {
      return;
    }

    this.isLoading = true;
    this.user = null;

    await new UserModel()
      .include(['review', 'user', 'address'])
      .find(this.$route.params.id)
      .then((user: UserModel) => {
        this.user = user;
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected toggleEdit(): void {
    if (! this.isEditing) {
      this.isEditing = true;
      return;
    }

    openDialog({
      title: 'Bewerken annuleren',
      text: 'Weet je zeker dat je het bewerken wilt annuleren? Wijzigingen zullen niet worden opgeslagen',
      type: 'warning',
      buttons: {
        cancel: { color: 'gray', text: 'Nee' },
        confirm: { color: 'error',
          text: 'Ja',
          action: () => {
            this.cancelEditing();
          } },
      },
    });
  }

  protected setActiveTab(): void {
    const urlParts = window.location.pathname.split('/');
    this.currentPath = `/${urlParts[1]}/${urlParts[2]}`;

    if (! urlParts[3]) {
      this.$router.replace(`${this.currentPath}/actions`);
    } else {
      this.activeTab = window.location.pathname;
    }
  }

  protected cancelEditing(): void {
    this.isEditing = false;
  }

  protected getStatusColor(key: string): string {
    return getStatusColor(key);
  }

  protected getPillColor(status: string): string {
    return getStatusColor(status);
  }

  protected getPillLabel(status: string): string {
    return getStatusLabel(status);
  }

  protected handleFieldChanged(field: MiFormField): void {
    let replacedValue = false;
    this.editedFormFields = this.editedFormFields.map((formField: MiFormField): MiFormField => {
      if (formField.key !== field.key) {
        return formField;
      }
      replacedValue = true;
      return { ...formField, ...{ value: field.value } };
    });
    if (replacedValue) {
      return;
    }
    this.editedFormFields.push(field);
  }

  protected async update(): Promise<void> {
    if (! this.isFormValid || this.isLoading) {
      return;
    }
    this.isLoading = true;
    const payload: CreateUserPayload = this.createUpdatePayload();
    const user: UserModel | null = await new UserModel({ id: this.$route.params.id })
      .update(payload)
      .catch((): null => null);
    if (! user) {
      return;
    }
    this.isLoading = false;

    this.user = merge(this.user, payload);

    this.cancelEditing();
    this.initialize();
  }

  protected createUpdatePayload(): CreateUserPayload {
    return this.editedFormFields.reduce(
      (previousValue: CreateUserPayload, field: MiFormField): CreateUserPayload => merge(
        previousValue,
        dotObject(
          {
            [field.key]: field.value,
          },
        ),
      ),
      {} as CreateUserPayload,
    );
  }

  protected get userFields(): MiFormField[] {
    if (! this.user) {
      return [];
    }

    return [
      {
        type: 'text',
        key: 'first_name',
        placeholder: 'Voornaam',
        value: this.user.first_name,
        label: 'Voornaam',
        rules: ['required'],
        class: 'w-1/2 pr-8',
      },
      {
        type: 'text',
        key: 'last_name',
        placeholder: 'Achternaam',
        value: this.user.last_name,
        label: 'Achternaam',
        rules: ['required'],
        class: 'w-1/2 pl-8',
      },
      {
        type: 'text',
        key: 'email',
        placeholder: 'Email',
        value: this.user.email ? this.user.email : '',
        label: 'Email',
        rules: ['required', 'email'],
      },
      {
        type: 'text',
        key: 'phone',
        placeholder: 'Telefoonnummer',
        value: this.user.phone ? this.user.phone : '',
        label: 'Telefoonnummer',
        rules: ['required', 'dutchPhone'],
      },
    ];
  }

  protected get tabs(): Tab[] {
    return [
      {
        name: 'Acties',
        key: 'actions',
        href: '/actions',
        visible: () => true,
      },
    ];
  }

  // PUSHER
  /**
   * Initalize Pusher room and listen for events
   */
   private pusher: PusherService | null = null;

   private initializePusher() {
     if (! Application.config('APP_APPLICATION_MODE') || ! this.$store.state.Auth) {
       return;
     }

     this.pusher = (new PusherService() as PusherService)
       .author(this.$store.state.Auth)
       .room(Application.config('APP_APPLICATION_MODE') as string)
       .echo()
       .on('user:sync.started', (event: UserSyncStarted) => {
         this.handleSyncStarted(event.user);
       })
       .on('user:sync.finished', (event: UserSyncFinished) => {
         this.handleSyncFinished(event.user);
       })
       .on('user:sync.failed', (event: UserSyncFailed) => {
         this.handleSyncFailed(event.user, event.error);
       });
   }

   private handleSyncStarted(syncUser: UserModel) {
     if (this.user && syncUser.uuid !== this.user.uuid) {
       return;
     }

     this.isSyncing = true;
     this.$root.$emit('syncUser', true);
   }

   private handleSyncFinished(syncUser: UserModel) {
     if (this.user && syncUser.uuid !== this.user.uuid) {
       return;
     }

     this.isSyncing = false;
     this.$root.$emit('syncUser', false);
   }

   private handleSyncFailed(syncUser: UserModel, error: string) {
     if (this.user && ((syncUser.uuid !== this.user.uuid) || ! error)) {
       return;
     }

     this.isSyncing = false;
     this.$root.$emit('syncUser', false);
   }
}

interface Tab {
  name: string;
  key: string;
  href: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  visible: Function;
}
